import React, { useMemo } from "react";
import { ShoppingBag, XCircle, Timer } from "lucide-react";
import { alvoTour } from "../../tour";
import { INT, PCT, MESES, dataLocal, diaSemanaSeg } from "../formato";
import { projecaoDiaria, restanteDeHoje } from "../calculos";
import { LinhaAnoPassado } from "./VendasHoje";
import { BlocoCarregando, CartaoCarregando } from "./Carregando";
import FaturamentoPorDia from "./FaturamentoPorDia";
import MapaDeCalor from "./MapaDeCalor";

export default function RelatoriosMes({ mes, horarios }) {
  const titulo = mes ? `${MESES[dataLocal(mes.hoje).getMonth()]} de ${dataLocal(mes.hoje).getFullYear()}` : "";
  return (
    <section ref={alvoTour("vg-relatorios")} className="vg-section">
      <div className="vg-section-head">
        <h2>Relatórios do mês {titulo && <span className="vg-sub-h2">· {titulo}</span>}</h2>
      </div>
      <div className="vg-today">
        {!mes ? Array.from({ length: 3 }, (_, i) => <CartaoCarregando key={i} />) : <Cartoes mes={mes} />}
      </div>
      <article className="vg-card" style={{ marginTop: 16 }}>
        <div className="vg-card-head"><div><h3>Faturamento por dia</h3></div></div>
        {!mes ? <BlocoCarregando altura={280} /> : <FaturamentoPorDia mes={mes} />}
      </article>
      <article ref={alvoTour("vg-mapa-calor")} className="vg-card vg-heat-wrap" style={{ marginTop: 16 }}>
        <div className="vg-card-head"><div><h3>Pedidos por dia da semana e hora</h3></div></div>
        {!mes ? <BlocoCarregando altura={220} /> : <MapaDeCalor mes={mes} horarios={horarios} />}
      </article>
    </section>
  );
}

function Cartoes({ mes }) {
  const { atual: a, ano_passado: ap } = mes.cartoes;
  const tem = ap.tem_dado;
  const lim = mes.limite_preparo_min;
  const dica = ap.ate ? `Ano passado, do dia 1 até a mesma data, às ${ap.ate}` : undefined;
  const projPedidos = useMemo(() => {
    const { proj } = projecaoDiaria({
      valores: mes.dias.map((d) => d.pedidos),
      diasNoMes: mes.dias_no_mes,
      esperadoRestanteHoje: restanteDeHoje(mes, "pedidos"),
      diaSemana: (d) => diaSemanaSeg(mes.dias[0].data.slice(0, 8) + String(d).padStart(2, "0")),
    });
    const passados = mes.dias.slice(0, -1).reduce((s, d) => s + d.pedidos, 0);
    return Math.round(passados + Object.values(proj).reduce((s, v) => s + v, 0));
  }, [mes]);
  const pct = projPedidos ? Math.round((a.pedidos / projPedidos) * 100) : 0;

  return (
    <>
      <div className="vg-tk">
        <div className="vg-tk-top"><span className="vg-tk-label">Pedidos no mês</span><span className="vg-tk-ic"><ShoppingBag className="w-[17px] h-[17px]" strokeWidth={1.8} /></span></div>
        <div className="vg-tk-value">{INT(a.pedidos)}</div>
        <div className="vg-tk-meta">
          <div className="vg-tk-row"><span>Projeção do mês <b>{INT(projPedidos)}</b></span><b className="pct">{pct}%</b></div>
          <div className="vg-pbar"><i style={{ width: `${Math.min(100, pct)}%` }} /></div>
        </div>
        <LinhaAnoPassado dica={dica} atual={a.pedidos} passado={ap.pedidos} temDado={tem} fmt={INT} />
      </div>
      <div className="vg-tk">
        <div className="vg-tk-top"><span className="vg-tk-label">Cancelados no mês</span><span className="vg-tk-ic"><XCircle className="w-[17px] h-[17px]" strokeWidth={1.8} /></span></div>
        <div className="vg-tk-value">{INT(a.cancelados)}</div>
        <div className="vg-tk-meta"><div className="vg-tk-row"><span>Taxa de cancelamento <b>{PCT(a.taxa_cancelamento)}</b></span></div></div>
        <LinhaAnoPassado dica={dica} atual={a.cancelados} passado={ap.cancelados} temDado={tem} menorMelhor
          fmt={(v) => `${INT(v)} (${PCT(ap.taxa_cancelamento)})`} />
      </div>
      <div className="vg-tk">
        <div className="vg-tk-top"><span className="vg-tk-label">Tempo médio de preparo</span><span className="vg-tk-ic"><Timer className="w-[17px] h-[17px]" strokeWidth={1.8} /></span></div>
        <div className={`vg-tk-value ${a.tempo_preparo_min != null && a.tempo_preparo_min > lim ? "bad" : ""}`}>
          {a.tempo_preparo_min == null ? "—" : <>{a.tempo_preparo_min} <small>min</small></>}
        </div>
        <div className="vg-tk-meta"><div className="vg-tk-row"><span>Limite <b>{lim} min</b></span></div></div>
        <LinhaAnoPassado dica={dica} atual={a.tempo_preparo_min ?? 0} passado={ap.tempo_preparo_min} temDado={tem && ap.tempo_preparo_min != null}
          fmt={(v) => `${INT(v)} min`} menorMelhor delta="abs" />
      </div>
    </>
  );
}
